import { useEffect, useMemo, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Requirement,LenderTag, StatusHistoryEntry, DisbursementTranche } from "../../types/deals";
import { AddLenderDialog, AddLenderValues } from "../requirements/AddLenderDialog";
import { LenderStatusDialog, StatusFormValues } from "../requirements/LenderStatusDialog";
import { LimitsTab } from "./LimitsTab";
import { DisbursementTab } from "./DisbursementTab";
import { IndianRupee, Wallet, Banknote, Clock, TrendingUp } from "lucide-react";
import { parseISO, addDays, addMonths, isBefore, isAfter } from "date-fns";
import { LenderCard } from "./LenderCard";
import { RequirementForm } from "../requirements/RequirementForm";
import { buildIdbUrl, idbPutFile } from "../../utils/idbFiles";
import apiService from "../../services/apiService";

interface RequirementDetailPanelProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  requirement: Requirement | null;
  onUpdate: (req: Requirement) => void;
  adminUserList: AdminUserList[];
}

interface AdminUserList {
  id: number;
  name: string;
};

interface LenderCompanyList {
  id: number;
  name: string;
};

export function RequirementDetailPanel({ open, onOpenChange, requirement, onUpdate,adminUserList }: RequirementDetailPanelProps) {
  const [addLenderOpen, setAddLenderOpen] = useState(false);
  const [statusOpenFor, setStatusOpenFor] = useState<string | null>(null);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [comments, setComments] = useState<{ id: string; text: string; by: string; timestamp: string }[]>([]);
  const [editReqOpen, setEditReqOpen] = useState(false);
  const [lenderCompanyList, setLenderCompanyList] = useState<LenderCompanyList[]>([]);
  const [disbursements, setDisbursements] = useState<DisbursementTranche[]>([]);
  const [isDisbursementsLoading, setIsDisbursementsLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (lenderCompanyList.length > 0) return;
    void fetchLenderCompanyList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    let cancelled = false;

    async function fetchDisbursedData() {
      if (!open || !requirement?.id) return;
      setIsDisbursementsLoading(true);
      try {
        const res: any = await apiService.get<any>(
          `/login/get-disbursed-data/${requirement.id}`
        );
        const list =
          (res?.data ?? res?.disbursements ?? res?.result ?? res) as
            | DisbursementTranche[]
            | undefined;
        if (!cancelled) setDisbursements(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!cancelled) setDisbursements([]);
      } finally {
        if (!cancelled) setIsDisbursementsLoading(false);
      }
    }

    fetchDisbursedData();
    return () => {
      cancelled = true;
    };
  }, [open, requirement?.id]);


  const summary = useMemo(() => {
    if (!requirement) return { sanction: 0, disbursed: 0, payout: { customer: 0, lender: 0, partner: 0 }, latestStatus: undefined, docs: [], monthlyRental: 0, maturedInThreeMonths: 0 };
    
    let sanction = 0;
    let disbursed = 0;
    const payout = { customer: 0, lender: 0, partner: 0 };
    let latestStatus: string | undefined = undefined;
    requirement.lenders.forEach((l) => {
      latestStatus = l.statusHistory[l.statusHistory.length - 1]?.status || latestStatus;
      const sanc = [...l.statusHistory].reverse().find((e) => typeof e.sanctionAmount === 'number')?.sanctionAmount;
      if (typeof sanc === 'number') sanction += sanc;
      const disbEntry = [...l.statusHistory].reverse().find((e) => typeof e.disbursedAmount === 'number');
      if (disbEntry) {
        disbursed += disbEntry.disbursedAmount ?? 0;
        payout.customer += disbEntry.customerPayout ?? 0;
        payout.lender += disbEntry.lenderPayout ?? 0;
        payout.partner += disbEntry.partnerPayout ?? 0;
      }
    });
    const docs = requirement.lenders.flatMap((l) => l.statusHistory.flatMap((e) => {
      const out: { label: string; name: string; url?: string }[] = [];
      if (e.termSheetName) out.push({ label: 'Term Sheet', name: e.termSheetName, url: e.termSheetUrl });
      if (e.sanctionLetterName) out.push({ label: 'Sanction Letter', name: e.sanctionLetterName, url: e.sanctionLetterUrl });
      return out;
    }));

    // Calculate monthly rental & matured in 3 months from disbursements
    const today = new Date();
    const next90Days = addDays(today, 90);
    let monthlyRental = 0;
    let maturedInThreeMonths = 0;
    (disbursements || []).forEach((d) => {
      monthlyRental += d.monthlyRental;
      const maturedOn = d.maturedOn 
        ? parseISO(d.maturedOn)
        : d.commencementDate 
          ? addMonths(parseISO(d.commencementDate), d.numberOfRentals || 12)
          : null;
      const isMatured = maturedOn && isAfter(today, maturedOn);
      if (maturedOn && !isMatured && isBefore(maturedOn, next90Days)) {
        maturedInThreeMonths++;
      }
    });

    return { sanction, disbursed, payout, latestStatus, docs, monthlyRental, maturedInThreeMonths };
  }, [requirement, disbursements]);

  if (!requirement) return null;

  const req = requirement;
  const isHybrid = req.type === "Hybrid";
  const canEditRequirement = req.lenders.length === 0;

  function handleAddLender(values: AddLenderValues) {
    const now = new Date().toISOString();
    const newTag: LenderTag = {
      id: Math.random().toString(36).slice(2, 8).toUpperCase(),
      requirementId: req.id,
      customerId: req.customerId ?? null,
      institution: values.institution,
      institutionName: values.institution,
      lenderName: values.lenderName,
      lenderUserName: values.lenderName,
      programLimit: values.programLimit,
      applicationRM: values.applicationRM,
      note: values.note,
      taggedAt: now,
      statusHistory: [{ status: "1", statusId: 1, timestamp: now, by: req.applicationRM }],
      comments: [],
    };
    onUpdate({ ...req, lenders: [newTag, ...req.lenders] });
    setAddLenderOpen(false);
  }

  function handleStatusUpdate(lenderId: string, vals: StatusFormValues) {
    const now = new Date().toISOString();
    const updated = req.lenders.map((l) => {
      if (l.id !== lenderId) return l;
      const entry: StatusHistoryEntry = {
        status: vals.status!,
        timestamp: now,
        by: req.applicationRM,
        note: vals.note,
        reason: vals.reason,
      };
      if (vals.termSheetFile) {
        const file = vals.termSheetFile as File;
        entry.termSheetName = file.name;
        entry.termSheetUrl = URL.createObjectURL(file);
      }
      if (typeof vals.sanctionAmount === "number") {
        entry.sanctionAmount = vals.sanctionAmount;
      }
      if (vals.sanctionLetterFile) {
        const file = vals.sanctionLetterFile as File;
        entry.sanctionLetterName = file.name;
        entry.sanctionLetterUrl = URL.createObjectURL(file);
      }
      if (typeof vals.disbursedAmount === "number") {
        entry.disbursedAmount = vals.disbursedAmount;
        entry.payoutBasis = vals.payoutBasis;
        
        // Handle percentage vs manual mode
        if (vals.payoutBasis === 'manual') {
          entry.customerPayout = vals.customerPayout;
          entry.lenderPayout = vals.lenderPayout;
          entry.partnerPayout = vals.partnerPayout;
        } else {
          // Calculate amounts from percentages
          const baseAmount = vals.payoutBasis === "sanctioned" 
            ? (entry.sanctionAmount ?? 0)
            : vals.disbursedAmount;
          
          entry.customerPayoutPercent = vals.customerPayoutPercent;
          entry.lenderPayoutPercent = vals.lenderPayoutPercent;
          entry.partnerPayoutPercent = vals.partnerPayoutPercent;
          
          entry.customerPayout = ((vals.customerPayoutPercent ?? 0) / 100) * baseAmount;
          entry.lenderPayout = ((vals.lenderPayoutPercent ?? 0) / 100) * baseAmount;
          entry.partnerPayout = ((vals.partnerPayoutPercent ?? 0) / 100) * baseAmount;
        }
        
        // Calculate total revenue: customer + lender - partner
        const rev = (entry.customerPayout ?? 0) + (entry.lenderPayout ?? 0) - (entry.partnerPayout ?? 0);
        entry.totalRevenue = rev;
      }
      return { ...l, statusHistory: [...l.statusHistory, entry] };
    });
    onUpdate({ ...req, lenders: updated });
    setStatusOpenFor(null);
  }

  async function handleSanctionLetterUpload(lenderId: string, file: File) {
    const key = `lender:${lenderId}:sanctionLetter`;
    await idbPutFile(key, file);

    const updated = req.lenders.map((l) => {
      if (l.id !== lenderId) return l;
      if (l.statusHistory.length === 0) return l;
      const lastIdx = l.statusHistory.length - 1;
      const last = l.statusHistory[lastIdx];
      const patched = {
        ...last,
        sanctionLetterName: file.name,
        sanctionLetterUrl: buildIdbUrl(key),
      };
      const nextHistory = [...l.statusHistory];
      nextHistory[lastIdx] = patched;
      return { ...l, statusHistory: nextHistory };
    });
    onUpdate({ ...req, lenders: updated });
  }

  function handleAddDisbursement(disbursementData: Omit<DisbursementTranche, "id">) {
    const newDisbursement: DisbursementTranche = {
      ...disbursementData,
      id: Math.random().toString(36).slice(2, 8).toUpperCase(),
    };
    setDisbursements((prev) => [...prev, newDisbursement]);
  }

  // Calculate counts for tab badges
  const sanctionedLendersCount = req.lenders.filter((l) =>
    l.statusHistory.some(
      (e) =>
        (e.status === "8" || e.status === "9" || e.statusId === 8 || e.statusId === 9) &&
        typeof e.sanctionAmount === "number" &&
        e.sanctionAmount > 0
    )
  ).length;
  const disbursementsCount = disbursements.length || 0;


  const fetchLenderCompanyList = async () => {
    try {
      const response = await apiService.get<{ data: LenderCompanyList[] }>(`/customers/get-lender-company-list`);
      const transformedDocs = response.data.map((doc) => ({
        name: doc.name,
        id: doc.id
      }));
      setLenderCompanyList(transformedDocs);
    } catch (error) {
      console.error("Error fetching lender companies:", error);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle className="flex items-center justify-between">
            <span>Requirement #{requirement.id}</span>
            <Button size="icon" variant="secondary" onClick={() => setCommentsOpen(true)} aria-label="Open comments">
              {/* comment icon */}
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a4 4 0 0 1-4 4H7l-4 4V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z"/></svg>
            </Button>
          </SheetTitle>
        </SheetHeader>

        <div className="flex flex-col h-full max-h-[calc(100vh-120px)] overflow-auto mt-4">
          {/* Requirement Details Header - Always visible */}
           <Card className="mb-4">
            <CardContent className="py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="rounded-md border p-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="text-muted-foreground">Type</div>
                    <div><Badge variant="secondary">{requirement.type}</Badge></div>
                    <div className="text-muted-foreground">Quantum</div>
                    <div className="flex items-center gap-1"><IndianRupee className="h-4 w-4" /> {new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(requirement.quantum)}</div>
                    <div className="text-muted-foreground">Tenure</div>
                    <div>{requirement.tenure} mo</div>
                    <div className="text-muted-foreground">RM</div>
                    <div>{requirement.applicationRM}</div>
                    <div className="text-muted-foreground">Created</div>
                    <div>{new Date(requirement.createdAt).toLocaleDateString("en-GB")}</div>
                  </div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="text-muted-foreground">Products</div>
                    <div className="flex flex-wrap gap-1">{requirement.products.map((p) => (<Badge key={p} variant="outline">{p}</Badge>))}</div>
                    {requirement.dealNote && (
                      <>
                        <div className="text-muted-foreground">Note</div>
                        <div className="text-xs">{requirement.dealNote}</div>
                      </>
                    )}
                  </div>
                </div>
              </div>

               <div className="mt-4 flex justify-end">
                 <Button
                   variant="outline"
                   size="sm"
                   disabled={!canEditRequirement}
                   onClick={() => setEditReqOpen(true)}
                   title={!canEditRequirement ? "Editing is locked once lenders are tagged" : "Edit requirement"}
                 >
                   Edit Requirement
                 </Button>
               </div>
            </CardContent>
          </Card>

          {/* Content - Hybrid shows details directly, Leasing uses tabs */}
          {isHybrid ? (
            <div className="space-y-4 flex-1">
              {/* Enhanced Financial Summary */}
              <Card className="bg-gradient-to-br from-background to-muted/20">
                <CardContent className="py-6">
                  <div className="flex items-center gap-2 mb-4">
                    <IndianRupee className="h-5 w-5 text-primary" />
                    <h3 className="text-lg font-semibold">Financial Summary</h3>
                    {summary.latestStatus && (
                      <Badge 
                        variant={summary.latestStatus === 'Disbursed' ? 'default' : 
                                summary.latestStatus === 'Sanctioned' ? 'secondary' : 
                                summary.latestStatus === 'Rejected' ? 'destructive' : 'outline'}
                        className="ml-auto"
                      >
                        {summary.latestStatus}
                      </Badge>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    <div className="text-center p-3 rounded-lg bg-primary/5 border border-primary/10">
                      <div className="text-xs text-muted-foreground mb-1">Requested</div>
                      <div className="text-lg font-bold text-primary">
                        ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(requirement.quantum)}
                      </div>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-secondary/50 border border-secondary">
                      <div className="text-xs text-muted-foreground mb-1">Sanctioned</div>
                      <div className="text-lg font-bold text-secondary-foreground">
                        ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(summary.sanction)}
                      </div>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-accent/50 border border-accent">
                      <div className="text-xs text-muted-foreground mb-1">Disbursed</div>
                      <div className="text-lg font-bold text-accent-foreground">
                        ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(summary.disbursed)}
                      </div>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-primary/5 border border-primary/10">
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <Wallet className="h-3.5 w-3.5 text-primary" />
                      </div>
                      <div className="text-xs text-muted-foreground mb-1">Revenue</div>
                      <div className="text-lg font-bold text-primary">
                        ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(
                          summary.payout.customer + summary.payout.lender - summary.payout.partner
                        )}
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">C + L − P</div>
                    </div>
                  </div>

                  {(summary.payout.customer > 0 || summary.payout.lender > 0 || summary.payout.partner > 0) && (
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-medium mb-3 text-muted-foreground">Payout Breakdown</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="flex justify-between items-center p-3 rounded-md bg-muted/50">
                          <span className="text-sm text-muted-foreground">Customer Payout</span>
                          <span className="font-semibold text-primary">
                            ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(summary.payout.customer)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center p-3 rounded-md bg-muted/50">
                          <span className="text-sm text-muted-foreground">Lender Payout</span>
                          <span className="font-semibold text-primary">
                            ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(summary.payout.lender)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center p-3 rounded-md bg-muted/50">
                          <span className="text-sm text-muted-foreground">Partner Payout</span>
                          <span className="font-semibold text-destructive">
                            ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(summary.payout.partner)}
                          </span>
                        </div>
                      </div>
                      <div className="mt-4 p-3 rounded-md bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium">Total Revenue</span>
                          <span className="text-lg font-bold text-primary">
                            ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(
                              summary.payout.customer + summary.payout.lender - summary.payout.partner
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {summary.docs.length > 0 && (
                <Card>
                  <CardContent className="py-4">
                    <div className="text-sm font-medium mb-2">Documents</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      {summary.docs.map((d, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className="text-muted-foreground w-28">{d.label}</span>
                          {d.url ? (
                            <a className="underline" href={d.url} target="_blank" rel="noreferrer">{d.name}</a>
                          ) : (
                            <span>{d.name}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold">Tagged Lenders</h3>
                <Button variant="secondary" onClick={() => setAddLenderOpen(true)}>Add Lender</Button>
              </div>

              <div className="space-y-3">
                {requirement.lenders.map((l) => (
                  <LenderCard 
                    key={l.id ?? `${l.institution}-${l.lenderName}-${l.taggedAt}`} 
                    lender={l} 
                    onUpdateStatus={() => l.id && setStatusOpenFor(l.id)}
                    onSanctionLetterUpload={(file) => l.id && void handleSanctionLetterUpload(l.id, file)}
                    onNoteUpdate={(note) => {
                      const updated = requirement.lenders.map(lender => 
                        lender.id === l.id ? { ...lender, note } : lender
                      );
                      onUpdate({ ...requirement, lenders: updated });
                    }}
                    onCollateralUpdate={(details) => {
                      const updated = requirement.lenders.map(lender => {
                        if (lender.id !== l.id || lender.statusHistory.length === 0) return lender;
                        const lastIdx = lender.statusHistory.length - 1;
                        const patched = { ...lender.statusHistory[lastIdx], collateralDetails: details };
                        const nextHistory = [...lender.statusHistory];
                        nextHistory[lastIdx] = patched;
                        return { ...lender, statusHistory: nextHistory };
                      });
                      onUpdate({ ...requirement, lenders: updated });
                    }}
                  />
                ))}
                {requirement.lenders.length === 0 && (
                  <div className="text-sm text-muted-foreground border rounded-md p-6 text-center">No lenders tagged yet.</div>
                )}
              </div>
            </div>
          ) : (
            <Tabs defaultValue="details" className="flex-1">
              <TabsList className="grid w-full grid-cols-3 mb-4">
                <TabsTrigger value="details" className="text-sm">Details</TabsTrigger>
                <TabsTrigger value="limits" className="text-sm">
                  <Wallet className="h-4 w-4 mr-1.5" />
                  Limits
                  {sanctionedLendersCount > 0 && (
                    <Badge variant="secondary" className="ml-1.5 h-5 px-1.5 text-xs">{sanctionedLendersCount}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="disbursement" className="text-sm">
                  <Banknote className="h-4 w-4 mr-1.5" />
                  Disbursement
                  {isDisbursementsLoading && (
                    <Badge variant="outline" className="ml-1.5 h-5 px-1.5 text-xs">
                      Loading
                    </Badge>
                  )}
                  {disbursementsCount > 0 && (
                    <Badge variant="secondary" className="ml-1.5 h-5 px-1.5 text-xs">{disbursementsCount}</Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="space-y-4 mt-0">
                <Card className="bg-gradient-to-br from-background to-muted/20">
                  <CardContent className="py-6">
                    <div className="flex items-center gap-2 mb-4">
                      <IndianRupee className="h-5 w-5 text-primary" />
                      <h3 className="text-lg font-semibold">Financial Summary</h3>
                      {summary.latestStatus && (
                        <Badge 
                          variant={summary.latestStatus === 'Disbursed' ? 'default' : 
                                  summary.latestStatus === 'Sanctioned' ? 'secondary' : 
                                  summary.latestStatus === 'Rejected' ? 'destructive' : 'outline'}
                          className="ml-auto"
                        >
                          {summary.latestStatus}
                        </Badge>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                      <div className="text-center p-3 rounded-lg bg-primary/5 border border-primary/10">
                        <div className="text-xs text-muted-foreground mb-1">Requested</div>
                        <div className="text-lg font-bold text-primary">
                          ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(requirement.quantum)}
                        </div>
                      </div>
                      <div className="text-center p-3 rounded-lg bg-secondary/50 border border-secondary">
                        <div className="text-xs text-muted-foreground mb-1">Sanctioned</div>
                        <div className="text-lg font-bold text-secondary-foreground">
                          ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(summary.sanction)}
                        </div>
                      </div>
                      <div className="text-center p-3 rounded-lg bg-accent/50 border border-accent">
                        <div className="text-xs text-muted-foreground mb-1">Disbursed</div>
                        <div className="text-lg font-bold text-accent-foreground">
                          ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(summary.disbursed)}
                        </div>
                      </div>
                      <div className="text-center p-3 rounded-lg bg-primary/5 border border-primary/10">
                        <div className="flex items-center justify-center gap-1 mb-1">
                          <TrendingUp className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <div className="text-xs text-muted-foreground mb-1">Monthly Rental</div>
                        <div className="text-lg font-bold text-primary">
                          ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(summary.monthlyRental)}
                        </div>
                      </div>
                      <div className="text-center p-3 rounded-lg bg-muted/50 border border-muted">
                        <div className="flex items-center justify-center gap-1 mb-1">
                          <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                        </div>
                        <div className="text-xs text-muted-foreground mb-1">Maturing (3M)</div>
                        <div className="text-lg font-bold">{summary.maturedInThreeMonths}</div>
                      </div>
                    </div>

                    {(summary.payout.customer > 0 || summary.payout.lender > 0 || summary.payout.partner > 0) && (
                      <div className="border-t pt-4">
                        <h4 className="text-sm font-medium mb-3 text-muted-foreground">Payout Breakdown</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div className="flex justify-between items-center p-3 rounded-md bg-muted/50">
                            <span className="text-sm text-muted-foreground">Customer Payout</span>
                            <span className="font-semibold text-primary">
                              ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(summary.payout.customer)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center p-3 rounded-md bg-muted/50">
                            <span className="text-sm text-muted-foreground">Lender Payout</span>
                            <span className="font-semibold text-primary">
                              ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(summary.payout.lender)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center p-3 rounded-md bg-muted/50">
                            <span className="text-sm text-muted-foreground">Partner Payout</span>
                            <span className="font-semibold text-destructive">
                              ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(summary.payout.partner)}
                            </span>
                          </div>
                        </div>
                        <div className="mt-4 p-3 rounded-md bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium">Total Revenue</span>
                            <span className="text-lg font-bold text-primary">
                              ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(
                                summary.payout.customer + summary.payout.lender - summary.payout.partner
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {summary.docs.length > 0 && (
                  <Card>
                    <CardContent className="py-4">
                      <div className="text-sm font-medium mb-2">Documents</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        {summary.docs.map((d, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <span className="text-muted-foreground w-28">{d.label}</span>
                            {d.url ? (
                              <a className="underline" href={d.url} target="_blank" rel="noreferrer">{d.name}</a>
                            ) : (
                              <span>{d.name}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold">Tagged Lenders</h3>
                  <Button variant="secondary" onClick={() => setAddLenderOpen(true)}>Add Lender</Button>
                </div>

                <div className="space-y-3">
                  {requirement.lenders.map((l) => (
                    <LenderCard 
                      key={l.id ?? `${l.institution}-${l.lenderName}-${l.taggedAt}`} 
                      lender={l} 
                      onUpdateStatus={() => l.id && setStatusOpenFor(l.id)}
                      onSanctionLetterUpload={(file) => l.id && void handleSanctionLetterUpload(l.id, file)}
                      onNoteUpdate={(note) => {
                        const updated = requirement.lenders.map(lender => 
                          lender.id === l.id ? { ...lender, note } : lender
                        );
                        onUpdate({ ...requirement, lenders: updated });
                      }}
                      onCollateralUpdate={(details) => {
                        const updated = requirement.lenders.map(lender => {
                          if (lender.id !== l.id || lender.statusHistory.length === 0) return lender;
                          const lastIdx = lender.statusHistory.length - 1;
                          const patched = { ...lender.statusHistory[lastIdx], collateralDetails: details };
                          const nextHistory = [...lender.statusHistory];
                          nextHistory[lastIdx] = patched;
                          return { ...lender, statusHistory: nextHistory };
                        });
                        onUpdate({ ...requirement, lenders: updated });
                      }}
                    />
                  ))}
                  {requirement.lenders.length === 0 && (
                    <div className="text-sm text-muted-foreground border rounded-md p-6 text-center">No lenders tagged yet.</div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="limits" className="mt-0">
                <LimitsTab
                  lenders={requirement.lenders}
                  disbursements={disbursements}
                />
              </TabsContent>

              <TabsContent value="disbursement" className="mt-0">
                <DisbursementTab
                  lenders={requirement.lenders}
                  disbursements={disbursements}
                  onAddDisbursement={handleAddDisbursement}
                  requirementType={requirement.type}
                />
              </TabsContent>
            </Tabs>
          )}
        </div>
      </SheetContent>

      <AddLenderDialog admin_user_list={adminUserList} open={addLenderOpen} onOpenChange={setAddLenderOpen} institutions={lenderCompanyList} onSubmit={handleAddLender} />

      <LenderStatusDialog
        open={!!statusOpenFor}
        onOpenChange={(v) => !v && setStatusOpenFor(null)}
        onSubmit={(vals) => statusOpenFor && handleStatusUpdate(statusOpenFor, vals)}
        sanctionedAmountFromHistory={(() => {
          const l = req.lenders.find((x) => x.id === statusOpenFor);
          if (!l) return undefined;
          const e = [...l.statusHistory].reverse().find((e) => typeof e.sanctionAmount === 'number');
          return e?.sanctionAmount;
        })()}
        lenderInstitutionName={req.lenders.find((x) => x.id === statusOpenFor!)?.institutionName ?? req.lenders.find((x) => x.id === statusOpenFor!)?.institution}
        lenderUserName={req.lenders.find((x) => x.id === statusOpenFor!)?.lenderUserName ?? req.lenders.find((x) => x.id === statusOpenFor!)?.lenderName}
        currentStatus={(() => {
          const l = req.lenders.find((x) => x.id === statusOpenFor);
          const last = l?.statusHistory?.[l.statusHistory.length - 1]?.status;
          return (last ?? "1") as any;
        })()}
      />

      <RequirementForm
        open={editReqOpen}
        onOpenChange={(v) => !v && setEditReqOpen(false)}
        requirement={requirement}
        LenderStatus={[]}
        onSubmit={(vals) => {
          const v = vals as any;
          onUpdate({
            ...requirement,
            type: v.type,
            products: v.products,
            quantum: v.quantum,
            tenure: v.tenure,
            applicationRM: v.applicationRM,
            dealNote: v.dealNote,
          } as any);
          setEditReqOpen(false);
        }}
      />
      {/* Comments side sheet */}
      <Sheet open={commentsOpen} onOpenChange={setCommentsOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md space-y-4">
          <SheetHeader>
            <SheetTitle>Comments</SheetTitle>
          </SheetHeader>
          <div className="space-y-3 max-h-[60vh] overflow-auto pr-2">
            {comments.length === 0 && (
              <div className="text-sm text-muted-foreground">No comments yet.</div>
            )}
            {comments.map((c) => (
              <div key={c.id} className="rounded-md border p-3">
                <div className="text-xs text-muted-foreground mb-1">{new Date(c.timestamp).toLocaleString()} • {c.by}</div>
                <div className="text-sm">{c.text}</div>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Input placeholder="Add a comment" onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const val = (e.target as HTMLInputElement).value.trim();
                if (val) {
                  setComments((prev) => [{ id: Math.random().toString(36).slice(2,8), text: val, by: requirement.applicationRM, timestamp: new Date().toISOString() }, ...prev]);
                  (e.target as HTMLInputElement).value = '';
                }
              }
            }} />
            <Button onClick={() => {
              const input = document.querySelector<HTMLInputElement>('input[placeholder="Add a comment"]');
              const val = input?.value.trim();
              if (val) {
                setComments((prev) => [{ id: Math.random().toString(36).slice(2,8), text: val, by: requirement.applicationRM, timestamp: new Date().toISOString() }, ...prev]);
                if (input) input.value = '';
              }
            }}>Add</Button>
          </div>
        </SheetContent>
      </Sheet>
    </Sheet>
  );
}
